import { useEffect, useRef } from 'react';

/**
 * Accessible overlay modal.
 *
 * Props:
 *   open     — boolean: whether the modal is visible
 *   onClose  — function: called when the user dismisses the modal
 *   title    — string: displayed in the modal header
 *   children — modal body content
 */
function Modal({ open, onClose, title, children }) {
  const cardRef = useRef(null);

  /* Lock body scroll while open */
  useEffect(() => {
    if (open) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  /* Close on Escape key */
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  /* Return focus to trigger on close */
  const triggerRef = useRef(null);
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      // Focus the modal card so Escape works immediately
      setTimeout(() => cardRef.current && cardRef.current.focus(), 50);
    } else if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [open]);

  if (!open) return null;

  /* Backdrop click: only close if clicking the overlay itself, not the card */
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={handleBackdropClick}
    >
      <div
        className="modal-card"
        ref={cardRef}
        tabIndex={-1}
      >
        <div className="modal-head">
          <h2 id="modal-title" className="modal-title">{title}</h2>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            &#x2715;
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
}

export default Modal;
